import { ref, type Ref } from 'vue'
import { useToast } from '~/components/ui/toast'

export interface UseServerTTSReturn {
  isLoading: Ref<boolean>
  isPlaying: Ref<boolean>
  isPaused: Ref<boolean>
  duration: Ref<number>
  currentTime: Ref<number>
  play: (audioUrl: string) => void
  pause: () => void
  stop: () => void
  seek: (time: number) => void
}

interface AudioListeners {
  canplay: () => void
  play: () => void
  pause: () => void
  ended: () => void
  error: (e: Event) => void
  timeupdate: () => void
  loadedmetadata: () => void
}

export function useServerTTS(): UseServerTTSReturn {
  const isLoading = ref(false)
  const isPlaying = ref(false)
  const isPaused = ref(false)
  const duration = ref(0)
  const currentTime = ref(0)
  const { toast } = useToast()
  const { t } = useI18n()

  let audio: HTMLAudioElement | null = null
  let audioListeners: AudioListeners | null = null

  const cleanup = (): void => {
    if (audio && audioListeners) {
      // Remove all event listeners
      audio.removeEventListener('canplay', audioListeners.canplay)
      audio.removeEventListener('play', audioListeners.play)
      audio.removeEventListener('pause', audioListeners.pause)
      audio.removeEventListener('ended', audioListeners.ended)
      audio.removeEventListener('error', audioListeners.error)
      audio.removeEventListener('timeupdate', audioListeners.timeupdate)
      audio.removeEventListener('loadedmetadata', audioListeners.loadedmetadata)
      audioListeners = null
    }

    if (audio) {
      audio.pause()
      audio.src = ''
      audio = null
    }

    duration.value = 0
    currentTime.value = 0
  }

  const play = (audioUrl: string): void => {
    if (isLoading.value) return

    if (!audioUrl) {
      toast({
        title: t('error.title'),
        description: t('tts.no_audio_url', { defaultValue: 'Audio URL not found' }),
        variant: 'destructive'
      })
      return
    }

    if (audio && isPaused.value) {
      audio.play()
      isPaused.value = false
      isPlaying.value = true
      return
    }

    if (audio) {
      stop()
    }

    isLoading.value = true

    try {
      audio = new Audio(audioUrl)

      // Create listener object
      audioListeners = {
        canplay: () => {
          isLoading.value = false
        },
        play: () => {
          isPlaying.value = true
          isPaused.value = false
          isLoading.value = false
        },
        pause: () => {
          isPaused.value = true
          isPlaying.value = false
        },
        ended: () => {
          isPlaying.value = false
          isPaused.value = false
          cleanup()
        },
        error: (_e) => {
          isLoading.value = false
          isPlaying.value = false
          toast({
            title: t('error.title'),
            description: t('tts.playback_error'),
            variant: 'destructive'
          })
        },
        timeupdate: () => {
          if (audio) {
            currentTime.value = audio.currentTime
          }
        },
        loadedmetadata: () => {
          if (audio && isFinite(audio.duration)) {
            duration.value = audio.duration
          }
        }
      }

      // Attach listeners
      audio.addEventListener('canplay', audioListeners.canplay)
      audio.addEventListener('play', audioListeners.play)
      audio.addEventListener('pause', audioListeners.pause)
      audio.addEventListener('ended', audioListeners.ended)
      audio.addEventListener('error', audioListeners.error)
      audio.addEventListener('timeupdate', audioListeners.timeupdate)
      audio.addEventListener('loadedmetadata', audioListeners.loadedmetadata)

      audio.play().catch((err) => {
        isLoading.value = false
        toast({
          title: t('error.title'),
          description: t('tts.playback_error'),
          variant: 'destructive'
        })
        console.error('Audio playback error:', err)
      })
    } catch (error) {
      isLoading.value = false
      toast({
        title: t('error.title'),
        description: t('tts.audio_load_error'),
        variant: 'destructive'
      })
      console.error('Audio error:', error)
    }
  }

  const pause = (): void => {
    if (audio && isPlaying.value) {
      audio.pause()
      isPaused.value = true
      isPlaying.value = false
    }
  }

  const seek = (time: number): void => {
    if (audio) {
      audio.currentTime = time
      currentTime.value = time
    }
  }

  const stop = (): void => {
    cleanup()

    isPlaying.value = false
    isPaused.value = false
    isLoading.value = false
  }

  return {
    isLoading,
    isPlaying,
    isPaused,
    duration,
    currentTime,
    play,
    pause,
    stop,
    seek
  }
}
